<template lang="pug">
v-fade-transition(mode='out-in')
  #person
    section-hero-alt(:title='t("pages.team.title")')
    section-person-details(
      :person='person',
      :projects='projects',
      :bibtexString='bibtexString',
      :theses='theses'
     )
</template>

<script setup lang="ts">
import { createError, useAsyncData, useRoute, useRuntimeConfig } from 'nuxt/app'
import { PROJECT_STATUSES } from '~/constants/domain'
import type { Project } from '~/interfaces/project'
import { PersonalProjects } from '~/interfaces/project'
import { getAllTheses } from '~/services/hydratedThesesService'
import { getPersonByPath } from '~/services/peopleService'
import { publicAssetPath } from '~/utils/assets'
import {
  createAbsoluteUrl,
  createPageSchemaContext,
  createSeoMeta,
} from '~/utils/schema'

defineOptions({
  name: 'Person',
})

const route = useRoute()
const { t } = useI18n()
const slugSegments = Array.isArray(route.params.slug)
  ? route.params.slug
  : route.params.slug
    ? [route.params.slug]
    : []
const personPath = `/persons/${slugSegments.join('/')}`
const personDataKey = `person:${personPath}`

/// fetch person content
const { data: personData } = await useAsyncData(personDataKey, () =>
  getPersonByPath(personPath),
)

if (!personData.value) {
  throw createError({ statusCode: 404, message: 'Person not found' })
}

const person = personData.value
const { baseUrl, pageUrl } = createPageSchemaContext(
  route.path,
  useRuntimeConfig().public.baseUrl,
)
const personImage = person.picture
  ? createAbsoluteUrl(publicAssetPath(person.picture), baseUrl)
  : undefined
const researchFoci = (person.en.about.researchFoci ?? [])
  .filter(Boolean)
  .slice(0, 3)
const descriptionParts = [
  `${person.name.first} ${person.name.last} is ${person.en.role} at the Enterprise-of-Things Lab.`,
]

if (researchFoci.length) {
  descriptionParts.push(`Research focuses include ${researchFoci.join(', ')}.`)
}

useSeoMeta(
  createSeoMeta(
    `${person.name.first} ${person.name.last}`,
    descriptionParts.join(' '),
    pageUrl,
    personImage,
  ),
)

/// fetch matching finished and in progress projects
const { data: personProjects } = await useAsyncData<Project[]>(
  `person-projects:${person.personId}`,
  () =>
    queryCollection('projects')
      .where('projectId', 'IN', person.projects)
      .all() as Promise<Project[]>,
)

const allProjects = personProjects.value ?? []
const finishedProjects = allProjects.filter(
  (project) => project.projectStatus === PROJECT_STATUSES[1],
)
const inProgressProjects = allProjects.filter(
  (project) => project.projectStatus === PROJECT_STATUSES[0],
)
const projects = new PersonalProjects(inProgressProjects, finishedProjects)

/// fetch theses (as interface conforming) and filter out irrelevant ones
const thesesData = await getAllTheses()
const theses = thesesData.filter(
  (t) => t.supervisor === person.personId || t.tutor === person.personId,
)

/// bibtex url (if exists)
const bibtexString = person.publications?.bibtexUrl
  ? person.publications.bibtexUrl
  : ''
</script>
